import { Dayjs } from "dayjs";
import Reference from "yup/lib/Reference";

type Period = {
  dateStart?: Date | undefined;
  dateEnd?: Date | undefined;
};

export type DateLike =
  | Reference<unknown>
  | Date
  | Dayjs
  | string
  | number
  | null
  | undefined;
type PricePeriod = {
  dateStart?: Date | Dayjs | string | number | null | undefined;
  dateEnd?: Date | Dayjs | string | number | null | undefined;
};

export const hasOverlap = (periods: Period[]) => {
  const cleaned = periods
    .map((p) => {
      const s = p.dateStart != null ? new Date(p.dateStart).getTime() : NaN;
      const e = p.dateEnd != null ? new Date(p.dateEnd).getTime() : NaN;
      return Number.isFinite(s) && Number.isFinite(e) && s <= e
        ? { start: s, end: e }
        : Number.isFinite(s) && Number.isFinite(e) && s > e
        ? { start: e, end: s }
        : null;
    })
    .filter((p): p is { start: number; end: number } => !!p);

  if (cleaned.length < 2) return false;

  const sorted = [...cleaned].sort((a, b) => a.start - b.start);

  return sorted.some((p, i) => i > 0 && p.start <= sorted[i - 1].end);
};

const DAY = 24 * 60 * 60 * 1000;

export const toLocalDate = (d: DateLike) => {
  if (d == null) return null;
  if (d instanceof Date)
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const nd = new Date(d as any);
  return new Date(nd.getFullYear(), nd.getMonth(), nd.getDate());
};

export const dayIdx = (d: DateLike) => {
  const ld = toLocalDate(d);
  return ld ? Math.floor(ld.getTime() / DAY) : NaN;
};

export const findDayGaps = (
  periods: PricePeriod[],
  start: DateLike,
  end: DateLike,
  { allowTouching = true } = {}
) => {
  
  const W0 = dayIdx(start);
  const W1 = dayIdx(end);
  if (!Number.isFinite(W0) || !Number.isFinite(W1) || W0 > W1) {
    return { gaps: [{ from: W0, to: W1 }], ok: false };
  }

  const boxed = periods
    .map((p) => {
      const a = dayIdx(p.dateStart);
      const b = dayIdx(p.dateEnd);
      if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
      const from = Math.max(Math.min(a, b), W0);
      const to = Math.min(Math.max(a, b), W1);
      return from <= to ? { from, to } : null;
    })
    .filter(Boolean)
    .sort((x, y) => (x as any).from - (y as any).from) as {
    from: number;
    to: number;
  }[];

  const gaps: { from: number; to: number }[] = [];
  if (!boxed.length) return { gaps: [{ from: W0, to: W1 }], ok: false };

  if (boxed[0].from > (allowTouching ? W0 : W0 + 1)) {
    gaps.push({ from: W0, to: boxed[0].from - 1 });
  }

  let curFrom = boxed[0].from;
  let curTo = boxed[0].to;

  for (let i = 1; i < boxed.length; i++) {
    const it = boxed[i];
    const contiguous = allowTouching ? it.from <= curTo + 1 : it.from <= curTo;
    if (contiguous) {
      if (it.to > curTo) curTo = it.to;
    } else {
      gaps.push({ from: curTo + 1, to: it.from - 1 });
      curFrom = it.from;
      curTo = it.to;
    }
  }

  if (curTo < W1) gaps.push({ from: curTo + 1, to: W1 });

  return { gaps, ok: gaps.length === 0 };
};

export const fmtDay = (idx: number) => new Date(idx * DAY).toLocaleDateString();
